import React, { useCallback, useEffect, useRef, useState } from 'react';
import { crearApiKey, listarApiKeys, revocarApiKey } from './api';

// Sección de ajustes para las API keys de integración (ej. el Workspace IA de
// Skach). La clave en claro solo la devuelve el backend al crearla y se muestra
// una única vez; después solo queda su prefijo. El backend guarda únicamente el
// hash (ver backend/app/services/api_keys.py).
export default function ApiKeysSettings({ user, t }) {
  const label = (key, fallback) => {
    const text = t ? t(key) : '';
    return text && text !== key ? text : fallback;
  };

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [justCreated, setJustCreated] = useState(null); // clave en claro, una vez
  const [copied, setCopied] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null); // revocación inline
  const [revokingId, setRevokingId] = useState(null);
  const [error, setError] = useState('');
  const newKeyRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      setKeys(await listarApiKeys());
    } catch (e) {
      setError(label('apiKeysErrLoad', 'No se pudieron cargar las API keys.'));
    } finally {
      setLoading(false);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = async () => {
    setCreating(true);
    setError('');
    setJustCreated(null);
    setCopied(false);
    try {
      const res = await crearApiKey({ label: newLabel.trim() });
      setJustCreated(res.key);
      setNewLabel('');
      refresh();
    } catch (e) {
      setError((e && e.message) || label('apiKeysErrCreate', 'No se pudo crear la API key.'));
    } finally {
      setCreating(false);
    }
  };

  // Copia robusta: intenta la Clipboard API y, si falla (PWA / permisos),
  // cae a seleccionar el texto + execCommand('copy').
  const copy = async () => {
    const text = justCreated || '';
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok && newKeyRef.current) {
      try {
        newKeyRef.current.focus();
        newKeyRef.current.select();
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setError(label('apiKeysCopyManual', 'No se pudo copiar automáticamente; seleccioná el texto y copialo a mano.'));
      if (newKeyRef.current) {
        newKeyRef.current.focus();
        newKeyRef.current.select();
      }
    }
  };

  const revoke = async (id) => {
    setError('');
    setRevokingId(id);
    try {
      await revocarApiKey({ id });
      // Quita la key de la lista al instante, sin esperar el refresh.
      setKeys((prev) => prev.filter((k) => k.id !== id));
      setConfirmingId(null);
      refresh();
    } catch (e) {
      setError((e && e.message) || label('apiKeysErrRevoke', 'No se pudo revocar la API key.'));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="settings-section" id="settings-api-keys">
      <h3>{label('apiKeysSection', 'API keys')}</h3>
      <p className="settings-hint">
        {label(
          'apiKeysSectionHint',
          'Generá una clave para conectar wheniseeus con otras apps (ej. el Workspace IA de Skach). Pegala en la integración; no expira y podés revocarla cuando quieras.'
        )}
      </p>

      {!user && <p className="settings-hint">{label('apiKeysSignIn', 'Iniciá sesión para administrar tus API keys.')}</p>}

      {user && (
        <>
          <div className="settings-row">
            <input
              className="settings-input"
              type="text"
              placeholder={label('apiKeysLabelPlaceholder', 'Nombre (ej. Skach)')}
              value={newLabel}
              maxLength={80}
              onChange={(e) => setNewLabel(e.target.value)}
            />
            <button
              className="settings-action-btn"
              type="button"
              onClick={create}
              disabled={creating}
            >
              {creating ? label('apiKeysCreating', 'Generando…') : label('apiKeysCreate', 'Generar key')}
            </button>
          </div>

          {justCreated && (
            <div className="api-key-new" id="api-key-new">
              <strong className="settings-label">
                {label('apiKeysCopyNow', 'Copiala ahora — no se vuelve a mostrar:')}
              </strong>
              <div className="api-key-new-row">
                <input
                  ref={newKeyRef}
                  className="settings-input api-key-value"
                  type="text"
                  readOnly
                  value={justCreated}
                  onFocus={(e) => e.target.select()}
                />
                <button className="settings-action-btn" type="button" onClick={copy}>
                  {copied ? label('apiKeysCopied', 'Copiada ✓') : label('apiKeysCopy', 'Copiar')}
                </button>
              </div>
            </div>
          )}

          {loading && <p className="settings-hint">{label('apiKeysLoading', 'Cargando…')}</p>}
          {!loading && keys.length === 0 && (
            <p className="settings-hint">{label('apiKeysEmpty', 'Todavía no tenés API keys.')}</p>
          )}

          {keys.map((k) => (
            <div key={k.id} className="settings-row">
              <span className="settings-label">
                <span>{k.label || label('apiKeysUnnamed', 'Sin nombre')}</span>
                <code style={{ opacity: 0.7, fontSize: 12, marginLeft: 8 }}>{k.prefix}…</code>
              </span>
              {confirmingId === k.id ? (
                <span className="api-key-confirm">
                  <button
                    className="settings-action-btn"
                    type="button"
                    onClick={() => revoke(k.id)}
                    disabled={revokingId === k.id}
                  >
                    {revokingId === k.id
                      ? label('apiKeysRevoking', 'Revocando…')
                      : label('apiKeysConfirm', 'Confirmar')}
                  </button>
                  <button
                    className="settings-toggle-btn"
                    type="button"
                    onClick={() => setConfirmingId(null)}
                    disabled={revokingId === k.id}
                  >
                    {label('apiKeysCancel', 'Cancelar')}
                  </button>
                </span>
              ) : (
                <button
                  className="settings-action-btn"
                  type="button"
                  onClick={() => setConfirmingId(k.id)}
                >
                  {label('apiKeysRevoke', 'Revocar')}
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {error && <p className="settings-hint pin-error">{error}</p>}
    </div>
  );
}
